import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './msalConfig';
import { isEntitlementRefusal, markAccessDeniedAtRedirect } from './accessDenied';
import { installSessionResumeCheck } from './sessionResume';

let _instance: PublicClientApplication | null = null;
let _initPromise: Promise<PublicClientApplication> | null = null;

export function getMsalInstance(): PublicClientApplication | null {
  return _instance;
}

export async function initializeMsalInstance(): Promise<PublicClientApplication> {
  if (_instance) return _instance;
  if (_initPromise) return _initPromise;
  if (typeof window === 'undefined') {
    throw new Error('initializeMsalInstance must run on the client');
  }

  _initPromise = (async () => {
    const instance = new PublicClientApplication(msalConfig);

    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as { account?: { homeAccountId: string } };
        if (payload.account) {
          instance.setActiveAccount(instance.getAccount({ homeAccountId: payload.account.homeAccountId }));
        }
      }
    });

    await instance.initialize();

    try {
      const response = await instance.handleRedirectPromise();
      if (response?.account) {
        instance.setActiveAccount(response.account);
      }
    } catch (error) {
      if (isEntitlementRefusal(error)) {
        // #19758 — Entra refused the API token after acquireTokenRedirect (AADSTS50105): the
        // account signed in fine but is not entitled to the portal. Record it so the shell shows
        // Access denied and useAuth never redirects again. The cache is deliberately KEPT — the
        // account is valid, and clearing it would only send AuthGuard round a fresh loginRedirect
        // that succeeds and lands on the same refusal.
        markAccessDeniedAtRedirect();
        console.warn('[MSAL] API token refused (AADSTS50105) — showing Access denied');
      } else {
        // #19758 (the #14687 defect, fixed in the admin portal but not here) — this block used to
        // log "Cleared stale MSAL redirect state" and clear nothing. A failed handleRedirectPromise
        // leaves interaction state behind that blocks every later sign-in; actually clearing it
        // lets the app self-heal on reload.
        try {
          await instance.clearCache();
          console.warn('[MSAL] redirect handling failed — cleared stale interaction state');
        } catch {
          // Nothing left to clear, or storage is unavailable — never stop the app starting.
        }
      }
    }

    const accounts = instance.getAllAccounts();
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }

    // Bug #24685 — after a long absence, check the session as soon as the tab is visible again so an
    // expired session re-authenticates on return, not on the next random API call. Registered once
    // (this whole block runs once per page — _initPromise).
    installSessionResumeCheck(instance);

    _instance = instance;
    return instance;
  })();

  return _initPromise;
}
